import React from 'react';
import type { OptionGroup, OptionProp, OptionsGroup } from './normalize-options';

type RenderOptionArgs = {
    option: OptionProp;
    optionIndex: number;
    group: OptionGroup;
    groupIndex: number;
    key: string;
};

type RenderGroupArgs = {
    group: OptionGroup;
    groupIndex: number;
    key: string;
    children: React.ReactNode[];
};

type RenderEmpty = () => React.ReactNode;

type RenderOption = (args: RenderOptionArgs) => React.ReactNode;

type RenderGroup = (args: RenderGroupArgs) => React.ReactNode;

type RenderGroupedOptionsConfig = {
    groups?: OptionsGroup;
    renderOption: RenderOption;
    renderGroup?: RenderGroup;
    renderEmpty?: RenderEmpty;
};

const defaultRenderGroup: RenderGroup = ({ group, key, children }) => {
    if (!group.label) {
        return <React.Fragment key={key}>{children}</React.Fragment>;
    }

    return <React.Fragment key={key}>{children}</React.Fragment>;
};

export const renderGroupedOptions = ({ groups, renderOption, renderGroup, renderEmpty }: RenderGroupedOptionsConfig): React.ReactNode[] => {
    if (!groups || groups.length === 0) {
        if (!renderEmpty) return [];
        return [renderEmpty()];
    }

    const finalRenderGroup = renderGroup ?? defaultRenderGroup;

    return groups.flatMap((group, groupIndex) => {
        const optionNodes = group.options.map((option, optionIndex) => {
            const key = `${group.key}-${option.value}-${optionIndex}`;
            return renderOption({ option, group, groupIndex, optionIndex, key });
        });

        return finalRenderGroup({
            group,
            groupIndex,
            key: group.key ?? `${groupIndex}`,
            children: optionNodes,
        });
    });
};

export type { RenderGroupedOptionsConfig, RenderGroupArgs, RenderOptionArgs, RenderOption, RenderGroup, RenderEmpty };
export default renderGroupedOptions;
